/** Attached free text preserves the ordinary machine value of each choice. */
export interface OtherChoiceAnswer {
  value: string | string[];
  otherText: string;
}

/** Maximum attached text size, shared by typed and interpreted answers. */
export const OTHER_CHOICE_TEXT_LIMIT = 10000;

/** Shared model schema for the conditional text attached to a configured choice. */
export const OTHER_CHOICE_ANSWER_JSON_SCHEMA = {
  type: 'object', additionalProperties: false, required: ['value', 'otherText'],
  properties: {
    // Large nested array bounds exceed Gemini's schema-state limit. The
    // conversation engine validates configured options and selection limits.
    value: { anyOf: [{ type: 'string' }, { type: 'array', items: { type: 'string' } }] },
    otherText: { type: 'string', minLength: 1, maxLength: OTHER_CHOICE_TEXT_LIMIT },
  },
};

/** The model interprets meaning; the server validates the configured selection and text. */
export const OTHER_CHOICE_INSTRUCTION = 'When a choice explicitly configures Other (otherOptionId or otherOptionLabel), a reason outside the fixed choices can select that option. Return the combined answer as {value: selected exact label or array of exact labels, otherText: the respondent\'s specified text}. Include the configured Other label in value. Keep both parts together and respect selection limits. If Other detail is missing, ask what they want to specify and do not propose a complete answer. Never invent detail. Corrections replace both parts; omit otherText and use the ordinary string or array when Other is deselected. A plain option labelled Other without this setting remains a fixed choice. Select all means every configured option, only when maxSelected permits it, and still needs text if Other is included.';

/** Recognize the exact stored wrapper without coercing arbitrary answer objects. */
export function isOtherChoiceAnswer(raw: unknown): raw is OtherChoiceAnswer {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false;
  const answer = raw as Record<string, unknown>;
  return Object.keys(answer).length === 2 && typeof answer.otherText === 'string'
    && answer.otherText.trim().length > 0 && answer.otherText.length <= OTHER_CHOICE_TEXT_LIMIT
    && (typeof answer.value === 'string' || (Array.isArray(answer.value) && answer.value.every(value => typeof value === 'string')));
}

/** Other is opt-in, references an existing stable option and never extends a rating. */
export function isValidOtherChoiceConfig(field: Record<string, unknown>): boolean {
  return field.otherOptionId === undefined || ((field.type === 'choice_single' || field.type === 'choice_multi')
    && field.rating === undefined && typeof field.otherOptionId === 'string' && field.otherOptionId.length > 0
    && field.otherOptionId.length <= 200 && Array.isArray(field.options)
    && field.options.some(option => option && typeof option === 'object' && option.id === field.otherOptionId));
}

/** Display an accepted choice with its attached detail alongside the Other label. */
export function formatOtherChoiceAnswer(field: { type?: unknown; otherOptionId?: unknown; options?: unknown }, raw: unknown): string | undefined {
  const options = field.options;
  if (!isOtherChoiceAnswer(raw) || !Array.isArray(options)) return undefined;
  if ((field.type === 'choice_single' && typeof raw.value !== 'string')
    || (field.type === 'choice_multi' && !Array.isArray(raw.value))) return undefined;
  const values = Array.isArray(raw.value) ? raw.value : [raw.value];
  const selected = values.map(value => options.find(option => option && typeof option === 'object'
    && option.value === value && typeof option.label === 'string'));
  if (selected.some(option => !option) || !selected.some(option => option.id === field.otherOptionId)) return undefined;
  return selected.map(option => option.id === field.otherOptionId ? `${option.label}: ${raw.otherText.trim()}` : option.label).join(', ');
}
